import React, { useEffect, useRef } from 'react';
import { Award, Users, Building2, CheckCircle2, MapPin, Briefcase, Shield } from 'lucide-react';

const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right, .scale-in').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

const highlights = [
  { icon: Award, text: 'Experiencia desde 2012 · Empresa constituida en 2014' },
  { icon: Building2, text: '200+ proyectos: residencial, comercial y obra pública' },
  { icon: Users, text: 'Equipo técnico: arquitectos, ingenieros y residentes' },
  { icon: CheckCircle2, text: 'Calidad, costo y tiempo garantizados' },
];

const specialties = [
  'Construcción',
  'Arquitectura',
  'Obra Pública',
  'Obra Privada',
  'Remodelaciones',
  'Pavimentación',
  'Infraestructura',
  'Conservación y Mantenimiento',
];

const trustBlocks = [
  {
    icon: Award,
    value: 'Más de 12 años',
    label: 'de experiencia comprobada',
    sub: 'Operando desde 2012 · Empresa desde 2014',
  },
  {
    icon: MapPin,
    value: '200+',
    label: 'proyectos entregados',
    sub: 'Residencial, comercial y obra pública',
  },
  {
    icon: Briefcase,
    value: 'Gobierno + Grupo Salinas',
    label: 'entre nuestros clientes',
    sub: 'CEVI, SINFRA, SCT, Elektra, Banco Azteca',
  },
];

const About = () => {
  const ref = useScrollAnimation();

  return (
    <section
      id="nosotros"
      ref={ref}
      style={{
        position: 'relative',
        padding: '8rem 2rem',
        background: 'var(--bg-primary)',
        overflow: 'hidden',
      }}
    >
      {/* BG Accent */}
      <div style={{
        position: 'absolute',
        right: 0,
        top: 0,
        width: '40%',
        height: '100%',
        background: 'var(--bg-secondary)',
        clipPath: 'polygon(15% 0, 100% 0, 100% 100%, 0% 100%)',
        zIndex: 0,
      }} />

      <div style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: 1200,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '5rem',
        alignItems: 'center',
      }} className="about-grid">

        {/* ===== LEFT: Images ===== */}
        <div className="fade-left" style={{ position: 'relative', height: 520 }}>

          {/* Main image */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '80%',
            height: '75%',
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 30px 80px rgba(0,0,0,0.18)',
          }}>
            <img
              src="https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/ccmk3xor_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-05.jpg"
              alt="Grupo Constructor Yescas – Proyecto residencial"
              style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
              onMouseEnter={(e) => (e.target.style.transform = 'scale(1.04)')}
              onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
            />
          </div>

          {/* Secondary image */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '52%',
            height: '48%',
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            border: '4px solid white',
          }}>
            <img
              src="https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/q7hhk3dz_6_1.png"
              alt="Grupo Constructor Yescas – Área exterior"
              style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
              onMouseEnter={(e) => (e.target.style.transform = 'scale(1.05)')}
              onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
            />
          </div>

          {/* Floating badge */}
          <div
            className="float-anim"
            style={{
              position: 'absolute',
              top: '42%',
              right: '12%',
              background: 'var(--red)',
              color: 'white',
              borderRadius: 12,
              padding: '1rem 1.25rem',
              boxShadow: '0 10px 40px rgba(196,30,58,0.4)',
              textAlign: 'center',
              zIndex: 3,
            }}
          >
            <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 900, fontSize: '1.9rem', lineHeight: 1 }}>
              2012
            </div>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: '0.62rem',
              letterSpacing: '0.1em', textTransform: 'uppercase',
              opacity: 0.9, marginTop: 5,
            }}>
              Desde<br />siempre
            </div>
          </div>

          {/* Founded pill */}
          <div style={{
            position: 'absolute',
            bottom: '13%',
            left: 0,
            background: 'var(--bg-card)',
            border: '1.5px solid var(--border-color)',
            borderRadius: 40,
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            zIndex: 3,
          }}>
            <Shield size={14} color="var(--red)" strokeWidth={2} />
            <span style={{
              fontFamily: 'var(--font-heading)', fontWeight: 700,
              fontSize: '0.72rem', color: 'var(--text-primary)', letterSpacing: '0.04em',
            }}>
              Empresa registrada · 2014
            </span>
          </div>
        </div>

        {/* ===== RIGHT: Content ===== */}
        <div className="fade-right">
          <div className="section-label">Quiénes somos</div>

          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            color: 'var(--text-primary)',
            lineHeight: 1.15,
            marginBottom: '1.25rem',
          }}>
            Más de una década
            <span style={{ color: 'var(--red)', display: 'block' }}>construyendo Oaxaca.</span>
          </h2>

          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '1rem',
            color: 'var(--gray-dark)',
            lineHeight: 1.85,
            marginBottom: '0.875rem',
          }}>
            <strong>Grupo Constructor Yescas</strong> nació de la experiencia acumulada desde{' '}
            <strong>2012</strong>, formalizándose como empresa en <strong>2014</strong>. Somos
            una constructora oaxaqueña especializada en{' '}
            <strong>construcción, arquitectura e infraestructura</strong>, con presencia en
            proyectos de alto impacto en todo el estado.
          </p>

          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '1rem',
            color: 'var(--gray-dark)',
            lineHeight: 1.85,
            marginBottom: '0.875rem',
          }}>
            Atendemos proyectos <strong>públicos</strong> para dependencias como CEVI, SINFRA,
            SCT y Vivienda Bienestar; <strong>comerciales</strong> para Grupo Salinas (Elektra,
            Banco Azteca, Tiendas Neto) y Farmacias del Ahorro; y{' '}
            <strong>residenciales</strong> para familias en todo el estado. Nuestro enfoque es
            claro: <em>calidad, costo y tiempo</em>.
          </p>

          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '1rem',
            color: 'var(--gray-dark)',
            lineHeight: 1.85,
            marginBottom: '1.5rem',
          }}>
            Contamos con <strong>oficina administrativa propia</strong>, dos bodegas de{' '}
            <strong>120 m² y 200 m²</strong>, y un equipo técnico de arquitectos, ingenieros
            civiles, área de costos y residentes de obra capacitados para proyectos de
            cualquier escala.
          </p>

          {/* Specialty tags */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.75rem' }}>
            {specialties.map((s, i) => (
              <span
                key={i}
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: i < 3 ? 'var(--red)' : 'var(--gray-dark)',
                  background: i < 3 ? 'rgba(196,30,58,0.07)' : 'var(--bg-secondary)',
                  border: `1px solid ${i < 3 ? 'rgba(196,30,58,0.2)' : 'var(--border-color)'}`,
                  padding: '0.3rem 0.75rem',
                  borderRadius: 20,
                }}
              >
                {s}
              </span>
            ))}
          </div>

          {/* Highlights grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.75rem',
            marginBottom: '2rem',
          }}>
            {highlights.map((item, i) => (
              <div
                key={i}
                className={`fade-up delay-${i + 1}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  padding: '0.75rem',
                  borderRadius: 8,
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <item.icon size={18} color="var(--red)" strokeWidth={2} />
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8rem',
                  color: 'var(--text-primary)',
                  fontWeight: 500,
                }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-red"
          >
            Solicita una consulta
          </button>
        </div>
      </div>

      {/* ===== TRUST BLOCKS BAR ===== */}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1200, margin: '5rem auto 0' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          borderRadius: 16,
          overflow: 'hidden',
          border: '1px solid var(--border-color)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.07)',
        }} className="trust-blocks-grid">
          {trustBlocks.map((block, i) => {
            const Icon = block.icon;
            const isLast = i === trustBlocks.length - 1;
            return (
              <div
                key={i}
                className={`fade-up delay-${i + 1}`}
                style={{
                  padding: '2rem 1.75rem',
                  background: i === 0 ? 'var(--red)' : 'var(--bg-card)',
                  borderRight: !isLast ? '1px solid var(--border-color)' : 'none',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                }}
              >
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: i === 0 ? 'rgba(255,255,255,0.15)' : 'rgba(196,30,58,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon size={22} color={i === 0 ? 'white' : 'var(--red)'} strokeWidth={1.75} />
                </div>
                <div>
                  <div style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 900,
                    fontSize: '1.15rem',
                    color: i === 0 ? 'white' : 'var(--text-primary)',
                    lineHeight: 1.2,
                    marginBottom: '0.2rem',
                  }}>
                    {block.value}
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.85rem',
                    color: i === 0 ? 'rgba(255,255,255,0.9)' : 'var(--gray-dark)',
                    fontWeight: 500,
                    lineHeight: 1.4,
                    marginBottom: '0.3rem',
                  }}>
                    {block.label}
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.72rem',
                    color: i === 0 ? 'rgba(255,255,255,0.65)' : 'var(--gray)',
                    letterSpacing: '0.04em',
                  }}>
                    {block.sub}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .about-grid { grid-template-columns: 1fr !important; gap: 2rem !important; }
          .trust-blocks-grid { grid-template-columns: 1fr !important; border-radius: 12px !important; }
          .trust-blocks-grid > div { border-right: none !important; border-bottom: 1px solid var(--border-color); }
          .trust-blocks-grid > div:last-child { border-bottom: none !important; }
        }
        @media (max-width: 640px) {
          .about-grid > div:first-child { height: 300px !important; min-height: 300px !important; }
        }
      `}</style>
    </section>
  );
};

export default About;
